import { DialogComponent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';

/**
 * A custom element that manages a cart drawer.
 *
 * Fixes applied:
 * - Body scroll lock when drawer is open (prevents background scrolling on mobile)
 * - Installments CTA binding uses arrow function to preserve `this` context
 * - Tracks binding state to prevent duplicate installments listeners
 * - Checks event.detail.error before auto-opening (prevents empty drawer on failed add)
 * - Listens to native 'close' event to always restore scroll state
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {boolean} Tracks whether the installments CTA listener is bound */
  #isInstallmentsBound = false;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.addEventListener('close', this.#handleDialogClose);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.removeEventListener('close', this.#handleDialogClose);
    this.#unlockScroll();
  }

  /**
   * Handles the cart add event.
   * Only opens the drawer if the add-to-cart was successful.
   *
   * @param {CartAddEvent} event - The cart add event
   */
  #handleCartAdd = (event) => {
    if (event?.detail?.error) return;

    if (this.hasAttribute('auto-open')) {
      this.open();
    }
  };

  /**
   * Handles the native dialog close event.
   * Ensures scroll is always restored even if closed via Escape key or backdrop click.
   */
  #handleDialogClose = () => {
    this.#unlockScroll();
    this.#isInstallmentsBound = false;
  };

  /**
   * Locks the page scroll while the cart drawer is open.
   * Prevents the annoying background scroll on mobile devices.
   */
  #lockScroll() {
    document.body.classList.add('cart-drawer-open');
  }

  /**
   * Restores page scroll when the cart drawer closes.
   */
  #unlockScroll() {
    document.body.classList.remove('cart-drawer-open');
  }

  /**
   * Binds the installments CTA (Shop Pay) to close the drawer on click.
   * Uses an arrow function to preserve `this` context — fixes the original
   * bug where `this.closeDialog` was passed by reference, losing binding.
   * Tracks state via #isInstallmentsBound to prevent duplicate listeners.
   */
  #bindInstallmentsCTA() {
    if (this.#isInstallmentsBound) return;

    customElements.whenDefined('shopify-payment-terms').then(() => {
      const terms =
        this.querySelector('shopify-payment-terms') ||
        document.querySelector('shopify-payment-terms');
      const cta = terms?.shadowRoot?.querySelector('#shopify-installments-cta');

      if (cta) {
        this.#isInstallmentsBound = true;
        cta.addEventListener(
          'click',
          () => {
            this.close();
            this.#isInstallmentsBound = false;
          },
          { once: true }
        );
      }
    });
  }

  /**
   * Opens the cart drawer.
   */
  open() {
    this.#lockScroll();
    this.showDialog();
    this.#bindInstallmentsCTA();
  }

  /**
   * Closes the cart drawer.
   */
  close() {
    this.closeDialog();
    this.#unlockScroll();
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}

